import api from "./api";
import { normalizeVendorAssessmentInResponse } from "@/utils/vendor/normalizeVendorAssessmentResponse";
import { normalizeExistingSitesFromApi } from "@/utils/vendor/siteApiFieldUtils";
import {approvedVendors} from "../mocks/data/productService/vendor";
import { GeneratePackCopyV2Payload } from "@/components/ActiveProjects/PackCopy/types";
import { NfpDocumentRequestPayload, NfpDocumentSubmitPayload, ReactivateVendorPayload, ReviewCompletedPayload, SaveVendorAssessmentPayload, SubmitPackCopyPayload, SubmitPackCopyResponse, ExistingSite } from "@/types/VendorService.types";
import { IGetVendorUsersResponse, ICreateUpdateVendorUsersPayload } from "@/context/data.types";

const { vendorServiceAPI, productServiceAPI } = api;

// const GET_VENDOR_PATH = "/vendor/getVendor/";
const GET_VENDOR_PATH_FIRST = "/vendor/getVendor/";
const GET_VENDOR_PATH_SECOND = "/vendorAssessment/";
const GET_VENDOR_ASSESSMENT_PATH = "/vendor/getVendor/";
const DELETE_VENDOR_PATH = "/vendor/delete/";
const VENDOR_ASSESSMENT_DELETE_PATH = "/vendor/assessment/delete/";
const VENDOR_ASSESSMENT_CANCEL_PATH = "/vendor/cancel/";
const VENDOR_ASSESSMENT_FORM_ONLOAD_PATH = "vendor/assessment/form/onload";
const VA_GET_ASSESSMENT_FORM_DETAILS_PATH = "/vendor/assessment/form/";
const GET_GUEST_INGREDIENT_GUIDE_DATA_PATH = "/vendor/assessment/ingredient/guide";
const VA_GET_REQUIRED_DOCUMENTS_PATH = "/vendor/assessment/required/documents/";
const VA_GET_ASSESSMENT_INGREDIENTS_DETAILS_PATH = "/vendor/assessment/ingredients/";
const VA_GET_VENDOR_ASSESSMENT_CONTACT_INFO_PATH = "/vendor/assessment/contactInfo/";
const VA_GET_MANUFACTURING_FACILITY_INFO_PATH = "/vendor/assessment/manufacturingfacility";
const VA_GET_VENDOR_ASSESSMENT_ALL_PATH = "/vendor/all/assessment/";
const SAVE_VENDOR_ASSESSMENT_PATH = "/vendor/assessment/save/";
const INITIATE_VENDOR_TASK_PATH = "/vendor/initiate/assessment/";
const VENDOR_ASSESSMENT_REVIEW_COMPLETE_PATH = "/vendor/assessment/review/complete/";
const VENDOR_ASSESSMENT_REQUESTED_FOR_REVISION_PATH = "/vendor/assessment/request/revision/";
const VENDOR_INFORMATION_FORM_ONLOAD_PATH = "/vendor/assessment/vendor/onload";
const VENDOR_ONLOAD_PATH = "/vendor/onload";
const VENDOR_PRODUCT_SPECIFICATION_PATH = "/vendor/productspecification/";
const CREATE_VENDOR_PATH = "/vendor";
const REQUEST_NFP_DOCUMENT_PATH = "/vendor/assessment/request_nfp";
/** Same path as guest `VendorGuestService.submitNfpDocument`; authenticated PUT completes NFP upload for portal vendors. */
const SUBMIT_NFP_DOCUMENT_PATH = "/vendor/assessment/request_nfp/";
const GET_VENDOR_USERS_PATH = "/vendorUser";
const GET_VENDOR_USERS_BY_ID_PATH = "/vendorUser/";
const CREATE_UPDATE_VENDOR_USERS_PATH = "/vendorUser/";
const GET_VENDOR_USER_MANAGEMENT_ONLOAD_PATH = "/vendorUserManagement/onload";
const SAVE_MVA_PATH = "/vendor/upload/mva";
const GET_COUNTRIES_PATH = "/gold/onload";
const REACTIVATE_VENDOR_ASSESSMENT_PATH = "/vendor/assessment/reactivateVendorAssessment";
const REACTIVATE_VENDOR_PATH = "/vendor/assessment/reactivateVendor";
const GET_ALL_VENDOR_ASSESSMENT_PATH = "/vendor/assessment/";
const GET_ALL_NON_BLACKLISTED_VENDORS_PATH = "/vendor/getAllNonBlacklistedVendors";
const GET_EXISTING_SITES_PATH = "/vendor/getExistingSites";

// Deduplicate getVendor: one in-flight request per (vendorId, ideaId) so multiple callers get a single network call
const getVendorPromiseCache = new Map<string, Promise<any>>();

const VendorService = {
  
  getAllNonBlacklistedVendors: async (ideaId?: string | number): Promise<{ count: number; objects: Array<{ id: string; goldId: string; vendorCorporateName: string; vendorStatus: string }> }> => {
    try {
      const hasValidIdeaId =
        ideaId != null && String(ideaId).trim() !== "";
      const resp = hasValidIdeaId
        ? await vendorServiceAPI.get(GET_ALL_NON_BLACKLISTED_VENDORS_PATH, {
            params: { ideaId: String(ideaId) },
          })
        : await vendorServiceAPI.get(GET_ALL_NON_BLACKLISTED_VENDORS_PATH);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },

  getExistingSites: async (vendorId: string | number): Promise<ExistingSite[]> => {
    try {
      const resp = await vendorServiceAPI.get(`${GET_EXISTING_SITES_PATH}/${vendorId}`);
      return normalizeExistingSitesFromApi(resp?.data);
    } catch (error) {
      throw error;
    }
  },

  getVendor: async (vendorId: number, ideaId: number, assessmentId?: string | number | null) => {
    const hasValidAssessmentId =
      assessmentId != null &&
      String(assessmentId).trim() !== "" &&
      !Number.isNaN(Number(assessmentId));
    const key = `${vendorId}-${ideaId}${hasValidAssessmentId ? `-${assessmentId}` : ""}`;
    const existing = getVendorPromiseCache.get(key);
    if (existing) return existing;
    const promise = (async () => {
      try {
        const url = GET_VENDOR_PATH_FIRST + vendorId + GET_VENDOR_PATH_SECOND + ideaId;
        const resp = hasValidAssessmentId
          ? await vendorServiceAPI.get(url, { params: { assessmentId: String(assessmentId) } })
          : await vendorServiceAPI.get(url);
        return normalizeVendorAssessmentInResponse(resp.data, ideaId, assessmentId);
      } finally {
        getVendorPromiseCache.delete(key);
      }
    })();
    getVendorPromiseCache.set(key, promise);
    return promise;
  },
  getVendorAssessment: async (vendorId: string, ideaId: string | number) => {
    try {
      const hasValidIdeaId =
        ideaId !== undefined &&
        ideaId !== null &&
        String(ideaId).trim() !== "" &&
        !Number.isNaN(Number(ideaId));
      const path = hasValidIdeaId
        ? `${GET_VENDOR_ASSESSMENT_PATH}${vendorId}/vendorAssessment/${String(ideaId)}`
        : `${GET_VENDOR_ASSESSMENT_PATH}${vendorId}/vendorAssessment`;
      const resp = await vendorServiceAPI.get(path);
      return normalizeVendorAssessmentInResponse(
        resp.data,
        hasValidIdeaId ? ideaId : undefined
      );
    } catch (error) {
      throw error;
    }
  },
  deleteVendor: async (id: string) => {
    try {
      const resp = await vendorServiceAPI.post(DELETE_VENDOR_PATH + id);
      console.log("vendor deleted", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
    deleteVendorAssessment: async (id: string) => {
    try {
      const resp = await vendorServiceAPI.delete(VENDOR_ASSESSMENT_DELETE_PATH + id)
      return resp;
    } catch (error) {
      throw error;
    }
  },
  vendorAssessmentCancel: async (vendorId:string) => {
    try {
      const resp = await vendorServiceAPI.post(VENDOR_ASSESSMENT_CANCEL_PATH + vendorId);
      console.log("Vendor Cancelled ", resp.status); 
      return resp; 
    } catch (error) {
      throw error; 
    }
  },
  getOnLoadData: async () => {
    try {
      const resp = await vendorServiceAPI.get(VENDOR_ASSESSMENT_FORM_ONLOAD_PATH);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  getAllVendorAssessment: async (assessmentId: string) => {
    try {
      const resp = await vendorServiceAPI.get(GET_ALL_VENDOR_ASSESSMENT_PATH + assessmentId);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  // TODO: remove these sections when the new api integration is done on all screens
  getAssessmentFormDetails: async (id: string) => {
    try {
      const resp = await vendorServiceAPI.get(VA_GET_ASSESSMENT_FORM_DETAILS_PATH + id);
      console.log("Assessment Form Response", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getIngredientGuideData: async () => {
    try {
      const resp = await vendorServiceAPI.get(GET_GUEST_INGREDIENT_GUIDE_DATA_PATH);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  getRequiredDocuments: async (id: string) => {
    try {
      const resp = await vendorServiceAPI.get( VA_GET_REQUIRED_DOCUMENTS_PATH + id );
      console.log("Required Document Response", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getAssessmentIngredientsDetails: async (id: string) => {
    try {
      const resp = await vendorServiceAPI.get( VA_GET_ASSESSMENT_INGREDIENTS_DETAILS_PATH + id );
      console.log("Vendor Assessment Ingredients Details Response", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getVendorAssessmentContactInfo: async (id: string) => {
    try {
      const resp = await vendorServiceAPI.get(VA_GET_VENDOR_ASSESSMENT_CONTACT_INFO_PATH+id);
      console.log("Vendor Assessment Contact Info Response", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getVendorManufacturingFacilityInformation: async (id: string[]) => {
    try {
      const resp = await vendorServiceAPI.post(VA_GET_MANUFACTURING_FACILITY_INFO_PATH,id);
      console.log("Manufacturing facility information response", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getVendorAssessmentAll: async (id: number) => {
    try {
      const resp = await vendorServiceAPI.get(VA_GET_VENDOR_ASSESSMENT_ALL_PATH + id);
      console.log("Vendor Assessment All Response", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  saveVendorAssessment: async (vendorId: number, ideaId: number, SaveVendorAssessmentPayload:SaveVendorAssessmentPayload) => {
    try {
      const resp = await vendorServiceAPI.post(SAVE_VENDOR_ASSESSMENT_PATH + vendorId + "/idea/" + ideaId ,SaveVendorAssessmentPayload);
      console.log("Vendor assessment form submitted", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  saveMvaDocument: async (SaveVendorAssessmentPayload:SaveVendorAssessmentPayload) => {
    try {
      const resp = await vendorServiceAPI.post(SAVE_MVA_PATH, SaveVendorAssessmentPayload);
      console.log("mva document submitted", resp);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  initiateVendorTask: async (taskId:string,payload: object) => {
    try {
      console.log("payload",payload,taskId);
      const resp = await vendorServiceAPI.post(INITIATE_VENDOR_TASK_PATH + taskId, payload);
      console.log("resp:", resp)
      return resp;
    } catch (error) {
      throw error;
    }
  },
  vendorAssessmentReviewComplete: async (taskId: string, payload: ReviewCompletedPayload) => {
    try {
      const resp = await vendorServiceAPI.post(VENDOR_ASSESSMENT_REVIEW_COMPLETE_PATH + taskId, payload);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  vendorAssessmentRequestedForRevision: async (taskId: string, payload: ReviewCompletedPayload) => {
    try {
      const resp = await vendorServiceAPI.post(VENDOR_ASSESSMENT_REQUESTED_FOR_REVISION_PATH + taskId, payload);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getVendorInfoOnLoadData: async () => {
    try {
      const resp = await vendorServiceAPI.get(VENDOR_INFORMATION_FORM_ONLOAD_PATH);
      return resp.data;
    } catch (error) {
      throw error;
    }
  }, 
  getVendorOnload: async (): Promise<any> => {
    try {
      const resp = await vendorServiceAPI.get(VENDOR_ONLOAD_PATH);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  getVendorProductSpecification: async (productSpecId: string | number, vendorAssessmentId?: string | number): Promise<any> => {
    try {
      const base = `${VENDOR_PRODUCT_SPECIFICATION_PATH}${productSpecId}`;
      const url = vendorAssessmentId !== undefined && vendorAssessmentId !== null && String(vendorAssessmentId).trim() !== ''
        ? `${base}?vendorAssessmentId=${encodeURIComponent(String(vendorAssessmentId))}`
        : base;
      const resp = await vendorServiceAPI.get(url);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  updateVendorProductSpecification: async (productSpecId: string | number, payload: any): Promise<any> => {
    try {
      const resp = await vendorServiceAPI.put(`${VENDOR_PRODUCT_SPECIFICATION_PATH}${productSpecId}/storage`, payload);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  getApprovedVendorList: async (productId: string) => {
    try {
      // const resp = await vendorServiceAPI.get(`/vendor/approved/list?searchText=${searchText}`);
      const resp = {data:approvedVendors}; 
      console.log("Approved Vendor List Response", resp);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
   createVendor: async (vendor: object) => {
    try {
      const resp = await vendorServiceAPI.post(CREATE_VENDOR_PATH, vendor);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  updateVendor: async (vendor: object, vendorId: string) => {
    try {
      const resp = await vendorServiceAPI.put(`${CREATE_VENDOR_PATH}/${vendorId}`, vendor);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  requestNfpDocument: async (payload : NfpDocumentRequestPayload) => {
    try {
      const resp = await vendorServiceAPI.post(REQUEST_NFP_DOCUMENT_PATH, payload);
      return resp;
    } catch (error) {
      throw error;
    }
  },
  submitNfpDocument: async (vendorId: string, payload: NfpDocumentSubmitPayload) => {
    try {
      const resp = await vendorServiceAPI.put(
        `${SUBMIT_NFP_DOCUMENT_PATH}${vendorId}`,
        payload
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getVendorUsers: async (vendorId: string, vendorAssessmentId: string): Promise<IGetVendorUsersResponse> => {
    try {
      const resp = await vendorServiceAPI.get(`${GET_VENDOR_USERS_PATH}?vendorId=${vendorId}&vendorAssessmentId=${vendorAssessmentId}`);
      return resp.data;
    } catch (error) {
      console.error("Error fetching vendor users:", error);
      throw error;
    }
  },

  createUpdateVendorUsers: async (vendorId: string, payload: ICreateUpdateVendorUsersPayload): Promise<any> => {
    try {
      const resp = await vendorServiceAPI.post(`${CREATE_UPDATE_VENDOR_USERS_PATH}${vendorId}`, payload);
      return resp;
    } catch (error) {
      console.error("Error creating/updating vendor users:", error);
      throw error;
    }
  },
  getVendorUserManagementOnLoadData: async (): Promise<any> => {
    try {
      const resp = await vendorServiceAPI.get(`${GET_VENDOR_USER_MANAGEMENT_ONLOAD_PATH}`);
      return resp;
    } catch (error) {
      console.error("Error vendor user onload data:", error);
      throw error;
    }
  },
  getCountriesOnloadData: async (): Promise<any> => {
    try {
      const resp = await vendorServiceAPI.get(`${GET_COUNTRIES_PATH}`);
      return resp;
    } catch (error) {
      throw error;
    }
  },

  reactivateVendorAssessment: async (ideaId: number) => {
    try {
      const payload = {
        ideaId,
        transitionsRequest: {
          objectId: ideaId,
          transitionId: "activity_restart_idea",
        },
      };
      const resp = await vendorServiceAPI.post(REACTIVATE_VENDOR_ASSESSMENT_PATH, payload);
      return resp;
    } catch (error) {
      throw error;
    }
  },

  reactivateVendor: async (payload: ReactivateVendorPayload) => {
    try {
      const resp = await vendorServiceAPI.post(REACTIVATE_VENDOR_PATH, payload);
      return resp;
    } catch (error) {
      throw error;
    }
  },

  generatePackCopy: async (payload: {
    productSpecificationIds: number[];
    transitionsRequest: {
      transitionId: string;
      objectId: number;
      portalUserId: string;
    };
  }) => {
    try {
      const resp = await vendorServiceAPI.post("/vendor/productspecifications/generate-pack-copy", payload);
      return resp.data;
    } catch (error) {
      console.error("Error generating pack copy:", error);
      throw error;
    }
  },

  generatePackCopyV2: async (vendorAssessmentId: string | number, payload: GeneratePackCopyV2Payload) => {
    try {
      const resp = await vendorServiceAPI.post(`/packcopy/vendor-assessment/${vendorAssessmentId}/pack-copy/generate`, payload);
      return resp.data;
    } catch (error) {
      console.error("Error generating pack copy:", error);
      throw error;
    }
  },

  submitPackCopy: async (
    vendorAssessmentId: string | number,
    payload: {
      transitionsRequest?:
        | {
            transitionId: string;
            objectId: number;
            portalUserId: string;
          }
        | null;
      /** Omitted for first vendor revision (`versionNumber` null/1 from product info). */
      skippedArtwork?: boolean;
      /** Omitted for first vendor revision (`versionNumber` null/1 from product info). */
      skippedLegalReview?: boolean;
    }
  ): Promise<any> => {
    try {
      const resp = await vendorServiceAPI.post(
        `/packcopy/vendor-assessment/${vendorAssessmentId}/pack-copy/submit`,
        payload
      );
      return resp.data;
    } catch (error) {
      console.error("Error submitting pack copy:", error);
      throw error;
    }
  },

  getPackCopyFilter: async (vendorAssessmentId: string | number): Promise<any> => {
    try {
      const resp = await vendorServiceAPI.get(`/packcopy/vendor-assessment/${vendorAssessmentId}/pack-copy/filter`);
      return resp.data;
    } catch (error) {
      console.error("Error fetching pack copy filter:", error);
      throw error;
    }
  },

  requestRevisionPackCopy: async (
    vendorAssessmentId: string | number,
    payload: {
      transitionsRequest: {
        transitionId: string;
        objectId: number;
        portalUserId: string;
        revisionNoteId: number;
      };
    }
  ): Promise<void> => {
    try {
      const resp = await vendorServiceAPI.post(
        `/packcopy/vendor-assessment/${vendorAssessmentId}/requestRevision`,
        payload
      );
      return resp.data;
    } catch (error) {
      console.error("Error requesting revision for pack copy:", error);
      throw error;
    }
  },


};

export default VendorService;